import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * ErrorBoundary Component
 *
 * Catches render errors from its subtree and shows a recoverable panel
 * instead of unmounting the whole app. Mounted in Layout around <Outlet />
 * so a failing route breaks its own panel and leaves the nav intact.
 *
 * Must be a class — getDerivedStateFromError and componentDidCatch have no
 * hook equivalent.
 *
 * Note: React does NOT clear error state on its own. Without resetKey a
 * caught error would persist across navigation and the nav would look dead
 * even though it still works. Layout passes location.pathname, so moving to
 * another route clears the error and the new route gets a fresh attempt.
 *
 * @param {ReactNode} children - Subtree to guard
 * @param {any} resetKey - Changing this value clears a caught error
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  static getDerivedStateFromProps(props, state) {
    // Same instance, new route — drop the error so the subtree re-renders.
    if (state.error && props.resetKey !== state.resetKey) {
      return { error: null, resetKey: props.resetKey };
    }
    if (props.resetKey !== state.resetKey) {
      return { resetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error, info) {
    // Surfaced, never swallowed — this is the only record of the failure.
    console.error('Unhandled render error:', error, info?.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="flex items-center justify-center min-h-full p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 max-w-md w-full text-center">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-50 text-brand-red mx-auto mb-4">
            <AlertTriangle className="w-6 h-6" />
          </div>

          <h1 className="text-xl font-bold text-brand-charcoal tracking-tight">
            This page didn&apos;t load
          </h1>
          <p className="text-sm text-gray-500 mt-2 leading-relaxed">
            Something went wrong while rendering it. The rest of the app still
            works — use the navigation to go somewhere else, or reload to try
            again.
          </p>

          <button
            type="button"
            onClick={this.handleReload}
            className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-brand-red text-white font-semibold shadow-md shadow-brand-red/30 hover:bg-red-600 hover:shadow-lg hover:shadow-brand-red/40 transition-all duration-200"
          >
            <RefreshCw className="w-4 h-4" />
            Reload page
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
